import React from "react";
import TextInput from "./form-components/TextInput";
import Switch from "./form-components/Switch";
import Checkbox from "./form-components/Checkbox";
import SelectInput from "./form-components/SelectInput";
import { AmountInput } from "./form-components/TextInput";
import { useFormContext } from "./context/FormContext";
import ButtonSimple, { ButtonOutline } from "@/components/ui/button-simple";

const industries = [
  { label: "Automotive", value: "automotive" },
  { label: "Beauty & Personal Care", value: "beauty-personal-care" },
  { label: "Construction", value: "construction" },
  { label: "Consumer Electronics", value: "consumer-electronics" },
  { label: "Education", value: "education" },
  { label: "Energy & Utilities", value: "energy-utilities" },
  { label: "Events & Entertainment", value: "events-entertainment" },
  { label: "Fashion & Apparel", value: "fashion-apparel" },
  { label: "Financial Services", value: "financial-services" },
  { label: "Food & Beverage", value: "food-beverage" },
  { label: "Government & Public Sector", value: "government-public-sector" },
  { label: "Healthcare & Medical", value: "healthcare-medical" },
  { label: "Hospitality & Travel", value: "hospitality-travel" },
  { label: "Information Technology (IT)", value: "it" },
  { label: "Legal Services", value: "legal-services" },
  { label: "Logistics & Transportation", value: "logistics-transportation" },
  { label: "Manufacturing", value: "manufacturing" },
  { label: "Marketing & Advertising", value: "marketing-advertising" },
  { label: "Media & Publishing", value: "media-publishing" },
  { label: "Nonprofit & NGOs", value: "nonprofit-ngos" },
  { label: "Pharmaceuticals", value: "pharmaceuticals" },
  { label: "Real Estate", value: "real-estate" },
  { label: "Retail", value: "retail" },
  { label: "Sports & Recreation", value: "sports-recreation" },
  { label: "Telecommunications", value: "telecommunications" },
  { label: "Wholesale & Distribution", value: "wholesale-distribution" },
  { label: "Other", value: "other" },
];

const regions = [
  { label: "North America", value: "north-america" },
  { label: "Europe", value: "europe" },
  { label: "Asia Pacific", value: "asia-pacific" },
  { label: "Latin America", value: "latin-america" },
  { label: "Middle East & Africa", value: "middle-east-africa" },
];

const purchaseTimelines = [
  { label: "Multiple times per week", value: "multiple-times-week" },
  { label: "Weekly", value: "weekly" },
  { label: "Bi-weekly", value: "bi-weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Every 2-3 months", value: "every-2-3-months" },
  { label: "Quarterly", value: "quarterly" },
  { label: "A few times a year", value: "a-few-times-year" },
  { label: "As needed / One-off purchases", value: "as-needed-one-off" },
];

const BusinessBuyingProfile = () => {
  const { businessMethods, handleBack, handleFormSubmit, submitError } = useFormContext();
  const { register, formState, watch, setValue } = businessMethods;

  return (
    <form
      onSubmit={businessMethods.handleSubmit(data => handleFormSubmit(data, "business"))}
      className="mt-8 space-y-4"
    >
      <TextInput
        label="Company name"
        {...register("companyName")}
        error={formState.errors.companyName?.message}
      />

      <TextInput
        label="Company email"
        type="email"
        {...register("companyEmail")}
        error={formState.errors.companyEmail?.message}
      />

      <SelectInput
        label="Industry"
        value={watch("industry")}
        onChange={value => setValue("industry", value, { shouldValidate: true })}
        error={formState.errors.industry?.message}
        options={industries}
        placeholder="Select your industry"
      />

      <AmountInput
        label="Estimated purchase volume in USD"
        value={watch("purchaseVolume")}
        onChange={value => setValue("purchaseVolume", value, { shouldValidate: true })}
        error={formState.errors.purchaseVolume?.message}
        placeholder="Enter estimated volume"
      />

      <SelectInput
        label="Geographic region"
        value={watch("region")}
        onChange={value => setValue("region", value, { shouldValidate: true })}
        error={formState.errors.region?.message}
        options={regions}
        placeholder="Select your region"
      />

      <div className="pb-4 pt-4">
        <p className="mb-4 text-[15px] font-medium text-system-disabled">
          Share your notification preferences and future purchasing plans
        </p>

        <div>
          <p className="mb-[11px] text-sm text-system-titanium">Preferred notification method</p>
          <div className="flex gap-12">
            <Switch
              id="email-notifications"
              label="E-mail"
              checked={watch("notificationPreferences.email")}
              onCheckedChange={checked => setValue("notificationPreferences.email", checked)}
            />
            <Switch
              id="sms-notifications"
              label="SMS"
              checked={watch("notificationPreferences.sms")}
              onCheckedChange={checked => setValue("notificationPreferences.sms", checked)}
            />
          </div>
        </div>
      </div>

      <SelectInput
        label="How often does your business place orders for supplies or inventory?"
        value={watch("purchaseTimeline")}
        onChange={value => setValue("purchaseTimeline", value, { shouldValidate: true })}
        error={formState.errors.purchaseTimeline?.message}
        options={purchaseTimelines}
        placeholder="Select timeline"
      />

      <div className="pt-4">
        <Checkbox
          id="privacy-policy"
          checked={watch("privacyPolicy")}
          onCheckedChange={checked => setValue("privacyPolicy", checked, { shouldValidate: true })}
          error={formState.errors.privacyPolicy?.message}
          label={
            <>
              I confirm that I have read and agree to the{" "}
              <a href="/privacypolicy" target="_blank" className="text-system-primary">
                Privacy Policy
              </a>
            </>
          }
        />
      </div>

      <div className="flex gap-2.5 pt-2">
        <span>
          <ButtonOutline type="button" onClick={handleBack} className="h-[42px]">
            Go back
          </ButtonOutline>
        </span>
        <span>
          <ButtonSimple type="submit" className="h-[42px] rounded-lg text-white transition-colors">
            Join the waitlist
          </ButtonSimple>
        </span>
      </div>

      {submitError ? (
        <div className="mt-6 text-base font-medium text-red-500">{submitError}</div>
      ) : null}
    </form>
  );
};

export default BusinessBuyingProfile;
